/**
 * HOOK DE NOTIFICAÇÕES SSE
 * 
 * Hook personalizado para conectar ao stream SSE e gerenciar notificações
 * Caminho: frontend/src/hooks/useNotificacoes.js
 * 
 * ⭐ AGORA COM AUTENTICAÇÃO:
 * - Só conecta ao SSE se o usuário estiver autenticado (token JWT presente)
 * - Passa o token como query parameter na URL (EventSource não suporta headers customizados)
 * - Reconecta automaticamente se o token ainda for válido
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const useNotificacoes = () => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [conectado, setConectado] = useState(false);
  const [erro, setErro] = useState(null);
  const eventSourceRef = useRef(null);
  const timeoutRef = useRef(null);
  const { usuario } = useAuth();

  const conectarSSE = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.log('📡 ⚠️ Usuário não autenticado. Conexão SSE não estabelecida.');
      setConectado(false);
      setErro(null);
      return;
    }

    console.log('📡 Conectando ao stream de notificações...');
    
    // Criar conexão SSE
    const apiUrl = import.meta.env.VITE_API_URL;
    const isProduction = import.meta.env.VITE_IS_PRODUCTION === 'true';
    
    const baseStreamUrl = (isProduction || apiUrl === 'PRODUCAO')
      ? '/api/notificacoes/stream'                    // Produção
      : `${apiUrl || 'http://localhost:3001'}/api/notificacoes/stream`;  // Desenvolvimento
    
    // ⭐ ADICIONAR TOKEN COMO QUERY PARAMETER
    const streamUrl = `${baseStreamUrl}?token=${encodeURIComponent(token)}`;
    
    console.log('🔗 URL do stream SSE:', streamUrl.replace(token, 'TOKEN_OCULTO'));
    console.log('🔗 Ambiente:', isProduction ? 'PRODUÇÃO' : 'DESENVOLVIMENTO');
    
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    // Evento: Conexão aberta
    eventSource.onopen = () => {
      console.log('✅ Conectado ao stream SSE');
      setConectado(true);
      setErro(null);
    };

    // Evento: Mensagem recebida
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Notificação recebida:', data);

        // Aceitar notificações de pagamento PIX, upgrade de crédito, preço zerado, nome de produto inválido,
        // venda abaixo da política de preços e erro grave de custo médio zerado
        if (data.tipo === 'pagamento_pix' || data.tipo === 'upgrade_credito' || data.tipo === 'preco_zerado' || data.tipo === 'nome_produto_invalido' || data.tipo === 'erro_conexao_erp' || data.tipo === 'venda_abaixo_politica' || data.tipo === 'custo_medio_zerado' || data.tipo === 'aviso_pwa') {
          setNotificacoes((prev) => [data, ...prev]);
          
          // Tocar som de notificação (opcional)
          try {
            const audio = new Audio('/notification-sound.mp3');
            audio.volume = 0.3;
            audio.play().catch(() => {
              // Ignorar erro se som não estiver disponível
            });
          } catch (err) {
            // Ignorar
          }
        }
      } catch (err) {
        console.error('❌ Erro ao processar notificação:', err);
      }
    };

    // Evento: Erro
    eventSource.onerror = () => {
      console.error('❌ Erro na conexão SSE');
      setConectado(false);
      
      // Verificar se o token ainda existe (não houve logout)
      const tokenAindaExiste = localStorage.getItem('token');
      if (!tokenAindaExiste) {
        console.log('📡 ⚠️ Token removido. Não tentar reconectar SSE.');
        setErro(null);
        return;
      }
      
      setErro('Erro na conexão com servidor de notificações');
      
      // Fechar conexão
      eventSource.close();
      
      // Tentar reconectar após 5 segundos
      timeoutRef.current = setTimeout(() => {
        console.log('🔄 Tentando reconectar...');
        conectarSSE();
      }, 5000);
    };
  }, []);

  useEffect(() => {
    if (!usuario) {
      // Usuário deslogou: fechar conexão SSE existente
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (eventSourceRef.current) {
        console.log('🔌 Fechando conexão SSE (usuário deslogado)');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setConectado(false);
      setErro(null);
      setNotificacoes([]);
      return;
    }

    conectarSSE();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (eventSourceRef.current) {
        console.log('🔌 Fechando conexão SSE');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [usuario, conectarSSE]);

  // Remover notificação
  const removerNotificacao = (id) => {
    setNotificacoes((prev) => prev.filter((n) => n.id !== id));
  };

  // Limpar todas as notificações
  const limparNotificacoes = () => {
    setNotificacoes([]);
  };

  return {
    notificacoes,
    conectado,
    erro,
    removerNotificacao,
    limparNotificacoes
  };
};

export default useNotificacoes;